'use client';

import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { PRICE_HISTORY_RC_MEDIUM } from '@/lib/mockData';

// Backend integration point: fetch real price history by productId + weightKg
function getPriceHistory(productId: string, weightKg: number) {
  // For demo, return the RC Medium history scaled by weight ratio
  const baseWeight = 10;
  const ratio = weightKg / baseWeight;
  return PRICE_HISTORY_RC_MEDIUM.map((d) => ({
    ...d,
    price: parseFloat((d.price * ratio).toFixed(2)),
  }));
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: { value: number }[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-orange-200 rounded-xl shadow-lg px-4 py-3 text-sm">
      <p className="text-gray-500 text-xs mb-1">{label}</p>
      <p className="font-display font-bold text-orange-600 tabular-nums text-base">
        {payload[0].value.toFixed(2)} €
      </p>
      <p className="text-xs text-gray-400">Meilleur prix observé</p>
    </div>
  );
}

interface Props {
  productId: string;
  weightKg: number;
}

export default function PriceHistoryChart({ productId, weightKg }: Props) {
  const data = getPriceHistory(productId, weightKg);

  const prices = data.map((d) => d.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const currentPrice = prices[prices.length - 1];
  const firstPrice = prices[0];
  const trend = currentPrice - firstPrice;
  const trendPct = firstPrice > 0 ? ((trend / firstPrice) * 100).toFixed(1) : '0';
  const isDown = trend < 0;

  const yMin = Math.floor(minPrice * 0.95);
  const yMax = Math.ceil(maxPrice * 1.05);

  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-orange-100">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display font-bold text-gray-900 text-lg">Historique des prix</h2>
            <p className="text-sm text-gray-500 mt-0.5">90 derniers jours · Format {weightKg} kg</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-xs text-gray-400">Plus bas</p>
              <p className="font-display font-bold text-green-600 tabular-nums">{minPrice.toFixed(2)} €</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Plus haut</p>
              <p className="font-display font-bold text-red-500 tabular-nums">{maxPrice.toFixed(2)} €</p>
            </div>
            <div
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${
                isDown ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
              }`}
            >
              {isDown ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
              {isDown ? '' : '+'}
              {trendPct}% sur 90j
            </div>
          </div>
        </div>
      </div>

      <div className="p-5">
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f97316" stopOpacity={0.18} />
                <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#fde8d0" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={false}
              interval={2}
            />
            <YAxis
              domain={[yMin, yMax]}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v} €`}
              width={52}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={minPrice}
              stroke="#16a34a"
              strokeDasharray="4 4"
              strokeWidth={1.5}
              label={{ value: 'Min', position: 'insideTopRight', fontSize: 10, fill: '#16a34a' }}
            />
            <Area
              type="monotone"
              dataKey="price"
              stroke="#f97316"
              strokeWidth={2.5}
              fill="url(#priceGradient)"
              dot={false}
              activeDot={{ r: 5, fill: '#f97316', stroke: '#fff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}